import { useEffect, useRef } from 'react'
import * as d3 from 'd3'
import * as topojson from 'topojson-client'

const stateData = [
  { abbr: 'MA', name: 'Massachusetts' },
  { abbr: 'CT', name: 'Connecticut' },
  { abbr: 'ME', name: 'Maine' },
  { abbr: 'NH', name: 'New Hampshire' },
  { abbr: 'VT', name: 'Vermont' },
  { abbr: 'RI', name: 'Rhode Island' },
]

function NEMap() {
  const ref = useRef<SVGSVGElement>(null)

  useEffect(() => {
    const svg = d3.select(ref.current)
    svg.selectAll('*').remove()

    const NE_FIPS: Record<string, { color: string; accent: string; label: string }> = {
      '09': { color: '#1e3a5f', accent: '#2563eb', label: 'CT' },
      '23': { color: '#1a3a2a', accent: '#16a34a', label: 'ME' },
      '25': { color: '#1e2a3a', accent: '#06b6d4', label: 'MA' },
      '33': { color: '#2a1e3a', accent: '#7c3aed', label: 'NH' },
      '44': { color: '#3a2a1e', accent: '#ea580c', label: 'RI' },
      '50': { color: '#1a2a1e', accent: '#059669', label: 'VT' },
    }

    const width = ref.current?.parentElement?.offsetWidth || 400
    const height = ref.current?.parentElement?.offsetHeight || 400

    svg.attr('viewBox', `0 0 ${width} ${height}`)
       .attr('width', '100%')
       .attr('height', '100%')

    d3.json('./us-counties-10m.json').then((us: any) => {
      const counties = topojson.feature(us, us.objects.counties)
      const states   = topojson.feature(us, us.objects.states)

      const neCounties = (counties as any).features.filter((f: any) =>
        Object.keys(NE_FIPS).includes(String(f.id).padStart(5,'0').slice(0,2))
      )
      const neStates = (states as any).features.filter((f: any) =>
        Object.keys(NE_FIPS).includes(String(f.id).padStart(2,'0'))
      )

      const projection = d3.geoMercator().fitExtent(
        [[16, 16], [width - 16, height - 16]],
        { type: 'FeatureCollection', features: neCounties }
      )
      const path = d3.geoPath().projection(projection)

      // Counties
      svg.selectAll('path.county')
        .data(neCounties)
        .enter().append('path')
        .attr('class', 'county')
        .attr('d', path as any)
        .attr('fill', (d: any) => {
          const fips = String(d.id).padStart(5,'0').slice(0,2)
          return NE_FIPS[fips]?.color || '#1e2a3a'
        })
        .attr('stroke', '#21262d')
        .attr('stroke-width', 0.5)

      // State borders
      svg.selectAll('path.state')
        .data(neStates)
        .enter().append('path')
        .attr('class', 'state')
        .attr('d', path as any)
        .attr('fill', 'none')
        .attr('stroke', '#374151')
        .attr('stroke-width', 1.5)
        .attr('pointer-events', 'none')

      // State labels
      svg.selectAll('text.state-label')
        .data(neStates)
        .enter().append('text')
        .attr('class', 'state-label')
        .attr('x', (d: any) => (path.centroid(d) as any)[0])
        .attr('y', (d: any) => (path.centroid(d) as any)[1])
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', '11px')
        .attr('font-weight', '700')
        .attr('fill', (d: any) => {
          const fips = String(d.id).padStart(2,'0')
          return NE_FIPS[fips]?.accent || '#06b6d4'
        })
        .attr('pointer-events', 'none')
        .text((d: any) => NE_FIPS[String(d.id).padStart(2,'0')]?.label || '')
    })
  }, [])

  return <svg ref={ref} style={{ width: '100%', height: '100%', display: 'block' }} />
}

export default function CoverageSection() {
  return (
    <section
      id="coverage"
      className="relative py-[clamp(80px,12vh,160px)] bg-[#111827]"
    >
      <div className="max-w-[1280px] mx-auto px-6">
        <div className="coverage-header text-center mb-16">
          <span className="text-xs font-mono uppercase tracking-[0.08em] text-[#06b6d4]">
            COVERAGE
          </span>
          <h2 className="mt-4 text-[clamp(28px,3vw,42px)] font-semibold leading-[1.15] tracking-[-0.015em] text-white">
            All of New England. Every source that matters.
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[55%_45%] gap-12 items-start">
          {/* Map */}
          <div className="coverage-map relative rounded-xl overflow-hidden" style={{ width: '100%', height: '360px', background: '#111827' }}>
            <NEMap />
            {/* Scan line effect */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-xl">
              <div
                className="absolute left-0 right-0 h-px animate-scan-line"
                style={{
                  background: 'linear-gradient(90deg, transparent, #06b6d4, transparent)',
                }}
              />
            </div>
          </div>

          {/* State stats */}
          <div className="state-rows space-y-0">
            {stateData.map((state) => (
              <div
                key={state.abbr}
                className="state-row py-4 border-b border-white/[0.06]"
              >
                <div className="flex items-center gap-4 mb-2">
                  <span className="w-12 h-8 rounded-md bg-[#06b6d4]/10 flex items-center justify-center text-xs font-mono text-[#06b6d4] font-medium tracking-[0.04em]">
                    {state.abbr}
                  </span>
                  <span className="flex-1 text-base text-white">{state.name}</span>
                  <span className="text-base font-mono text-[#9ca3af] tracking-[0.04em]">
                    Daily
                  </span>
                </div>
                <div className="h-1 bg-white/[0.06] rounded-full overflow-hidden">
                  <div
                    className="state-bar-fill h-full bg-[#06b6d4] rounded-full w-full"
                  />
                </div>
              </div>
            ))}

            {/* Summary row */}
            <div className="state-row pt-6">
              <div className="flex items-center justify-between">
                <span className="text-base font-semibold text-white">
                  Every state, monitored daily
                </span>
              </div>
              <p className="mt-2 text-sm text-[#9ca3af] leading-relaxed">
                Municipal, county, state agency, housing authority, and transit
                sources across all six New England states.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
